// @flow
import map from 'lodash/map'
import { FormBuilderContext } from 'pages/Contexts/FormBuilderContext'
import * as React from 'react'
import Ui from 'ui'
import ToggleField from 'ui/molecules/ToggleField'
import Radio from 'ui/Radio'
import { QUESTION_TYPES } from 'utils/constants'
import useBuilderQuestion from 'utils/FormBuilder/hooks/useBuilderQuestion'

import useIntegration from '@/hooks/useIntegration'

import BuilderSideMenuEditQuestionSubtypeForChoice from './BuilderSideMenuEditQuestionSubtypeForChoice'
import BuilderSideMenuFileExtensions from './BuilderSideMenuFileExtensions'

function BuilderSideMenuEditQuestionSubtype(): React.Node {
  const { selectedItem, setQuestion } = React.useContext(FormBuilderContext)
  const { integration } = useIntegration()

  const question = useBuilderQuestion(selectedItem.id)
  const questionIntegration = question?.[`${integration}_integration`]

  const questionType = question?.question_type?.question_type
  const questionSubtype = question?.question_type?.question_subtype

  let validatedSubtype = question?.id_for_builder ?? questionType

  if (questionSubtype === 'calculation') {
    validatedSubtype = 'calculation'
  }

  const subtypes = QUESTION_TYPES[validatedSubtype]?.subtypes

  const onToggleMultiFileUpload = React.useCallback(
    (value: boolean) => {
      setQuestion(selectedItem.id, {
        ...question,
        multi_file_upload: value,
      })
    },
    [setQuestion, selectedItem, question]
  )

  React.useEffect(() => {
    if (!!questionIntegration && question.multi_file_upload) {
      onToggleMultiFileUpload(false)
    }
  }, [questionIntegration, onToggleMultiFileUpload, question.multi_file_upload])

  const onSelectSubtype = React.useCallback(
    (subtypeId) => {
      setQuestion(selectedItem.id, {
        ...question,
        question_type: {
          ...question?.question_type,
          question_subtype: subtypeId,
        },
      })
    },
    [setQuestion, selectedItem, question]
  )

  if (
    question?.question_type?.question_type !== 'file_upload' &&
    subtypes?.length <= 1
  )
    return null

  const labelsForQuestionTypes = {
    signature: 'Input',
  }

  if (questionType === 'radio' || questionType === 'checkbox') {
    return (
      <BuilderSideMenuEditQuestionSubtypeForChoice
        question={question}
        questionSubtype={questionSubtype}
        questionType={questionType}
      />
    )
  }

  if (questionType === 'file_upload') {
    if (questionSubtype === 'handout') {
      return null
    }

    return (
      <>
        <ToggleField
          horizontal
          primary
          disabled={!!questionIntegration}
          label="Allow Multiple Files"
          onChange={(e) => {
            onToggleMultiFileUpload(e.target.checked)
          }}
          value={question.multi_file_upload}
        />
        <BuilderSideMenuFileExtensions question={question} />
      </>
    )
  }

  return (
    <div>
      {labelsForQuestionTypes?.[questionType] && (
        <Ui.QuestionTitle>
          {labelsForQuestionTypes?.[questionType]}
        </Ui.QuestionTitle>
      )}
      {map(subtypes, ({ id, name }) => (
        <Radio
          checked={questionSubtype === id}
          key={id}
          onChange={() => onSelectSubtype(id)}
        >
          {name}
        </Radio>
      ))}
    </div>
  )
}

export default BuilderSideMenuEditQuestionSubtype
